/*
 * Dinowax carousel — shared by the migrated Liquid sections.
 *
 * Replaces Replo's React runtime for carousels. The track is a native
 * scroll-snap container, so it is fully usable with JavaScript disabled or
 * still loading; this file only adds the arrows, the dots and their state.
 *
 * Markup contract:
 *   [data-dw-carousel]
 *     [data-dw-carousel-track]   > direct children are the slides
 *     [data-dw-carousel-prev]    optional
 *     [data-dw-carousel-next]    optional
 *     [data-dw-carousel-dots]    optional, populated here
 *
 * Safe to load more than once — the guard below keeps a single definition and
 * init() only touches carousels it has not already wired up.
 */
(function () {
  'use strict';

  if (!window.DWCarousel) {
    var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

    function DWCarousel(root) {
      this.root = root;
      this.track = root.querySelector('[data-dw-carousel-track]');
      if (!this.track) return;

      this.prevBtn = root.querySelector('[data-dw-carousel-prev]');
      this.nextBtn = root.querySelector('[data-dw-carousel-next]');
      this.dotsEl = root.querySelector('[data-dw-carousel-dots]');
      this.dots = [];

      this.onScroll = throttle(this.update.bind(this), 100);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.step.bind(this, -1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.step.bind(this, 1));

      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(throttle(this.build.bind(this), 150));
        this.ro.observe(this.track);
      } else {
        window.addEventListener('resize', throttle(this.build.bind(this), 150));
      }

      this.build();
      root.setAttribute('data-dw-carousel-ready', 'true');
    }

    DWCarousel.prototype.slides = function () {
      return Array.prototype.filter.call(this.track.children, function (el) {
        return el.offsetParent !== null || el.getClientRects().length > 0;
      });
    };

    /* How many slides fit at once — drives the dot count. */
    DWCarousel.prototype.perView = function () {
      var slides = this.slides();
      if (!slides.length) return 1;
      var w = slides[0].getBoundingClientRect().width;
      if (!w) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / w));
    };

    DWCarousel.prototype.build = function () {
      var slides = this.slides();
      var scrollable = this.track.scrollWidth - this.track.clientWidth > 2;

      /* Nothing to scroll (e.g. the desktop grid) — stand down entirely. */
      this.root.setAttribute('data-dw-carousel-scrollable', scrollable ? 'true' : 'false');
      if (!scrollable) {
        if (this.dotsEl) this.dotsEl.innerHTML = '';
        this.dots = [];
        return;
      }

      if (this.dotsEl) {
        var pages = Math.max(1, slides.length - this.perView() + 1);
        if (this.dots.length !== pages) {
          this.dotsEl.innerHTML = '';
          this.dots = [];
          for (var i = 0; i < pages; i++) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'dw-carousel__dot';
            dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            dot.addEventListener('click', this.goTo.bind(this, i));
            this.dotsEl.appendChild(dot);
            this.dots.push(dot);
          }
        }
      }
      this.update();
    };

    DWCarousel.prototype.index = function () {
      var slides = this.slides();
      if (!slides.length) return 0;
      var left = this.track.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      var base = slides[0].offsetLeft;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(slides[i].offsetLeft - base - left);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      return best;
    };

    DWCarousel.prototype.update = function () {
      var i = this.index();
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var atStart = this.track.scrollLeft <= 2;
      var atEnd = this.track.scrollLeft >= maxScroll - 2;

      for (var d = 0; d < this.dots.length; d++) {
        var active = d === Math.min(i, this.dots.length - 1);
        this.dots[d].classList.toggle('is-active', active);
        this.dots[d].setAttribute('aria-current', active ? 'true' : 'false');
      }
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    };

    DWCarousel.prototype.scrollTo = function (left) {
      if (REDUCED.matches) this.track.scrollLeft = left;
      else this.track.scrollTo({ left: left, behavior: 'smooth' });
    };

    DWCarousel.prototype.goTo = function (i) {
      var slides = this.slides();
      if (!slides[i]) return;
      this.scrollTo(slides[i].offsetLeft - slides[0].offsetLeft);
    };

    DWCarousel.prototype.step = function (dir) {
      this.goTo(Math.max(0, this.index() + dir));
    };

    DWCarousel.prototype.destroy = function () {
      this.track.removeEventListener('scroll', this.onScroll);
      if (this.ro) this.ro.disconnect();
    };

    function throttle(fn, wait) {
      var queued = false;
      return function () {
        if (queued) return;
        queued = true;
        setTimeout(function () { queued = false; fn(); }, wait);
      };
    }

    DWCarousel.init = function (scope) {
      var root = scope || document;
      var nodes = root.querySelectorAll('[data-dw-carousel]:not([data-dw-carousel-ready])');
      Array.prototype.forEach.call(nodes, function (el) {
        el.dwCarousel = new DWCarousel(el);
      });
    };

    window.DWCarousel = DWCarousel;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { DWCarousel.init(); });
    } else {
      DWCarousel.init();
    }

    /* Theme editor: re-init on section load, and follow block selection. */
    document.addEventListener('shopify:section:load', function (e) { DWCarousel.init(e.target); });
    document.addEventListener('shopify:section:unload', function (e) {
      var el = e.target.querySelector('[data-dw-carousel]');
      if (el && el.dwCarousel) el.dwCarousel.destroy();
    });
    document.addEventListener('shopify:block:select', function (e) {
      var root = e.target.closest('[data-dw-carousel]');
      if (!root || !root.dwCarousel) return;
      var slides = root.dwCarousel.slides();
      var i = slides.indexOf(e.target);
      if (i > -1) root.dwCarousel.goTo(i);
    });
  } else {
    window.DWCarousel.init();
  }
})();
